import {
  createReportRandom,
  createSandboxReportModel,
  formatIsoDay,
  formatVisitorsDay,
  hashReportKey,
  reportMoney,
  SandboxDailyReportFact,
  SandboxReportModel,
  splitReportValue,
} from '../../reports/reportModel';
import { SandboxClient, SandboxWorker } from '../../store/types';
import { SandboxOperationHandler, SandboxVariables } from '../types';

type UnifiedData = { values: number[] };
type UnifiedReport = {
  title: string;
  labels: string[];
  keys: string[];
  extraLabels: string[];
  extraLabels2: string[];
  extraLabels3: string[];
  points: number;
  data: UnifiedData[];
  summary: UnifiedSummary[];
  url: null;
};
type UnifiedSummary = {
  title: string;
  value: number;
  extraValue: number | null;
  extraString: string | null;
};

const inputObject = (variables: SandboxVariables): Record<string, unknown> =>
  (variables.input as Record<string, unknown> | undefined) ?? {};

const summary = (
  title: string,
  value: number,
  extraValue: number | null = null,
  extraString: string | null = null
): UnifiedSummary => ({ title, value, extraValue, extraString });

const csvUrl = (rows: Array<Array<string | number>>): string => {
  const csv = rows
    .map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(';')
    )
    .join('\n');
  return `data:text/csv;charset=utf-8,%EF%BB%BF${encodeURIComponent(csv)}`;
};

const modelFor = (
  variables: SandboxVariables,
  store: Parameters<SandboxOperationHandler>[1]
): SandboxReportModel => createSandboxReportModel(variables, store);

const workShiftFio = (worker: SandboxWorker): string =>
  `${worker.first_name} ${worker.middle_name || 'А.'} ${worker.last_name}`;

interface GeneratedShift {
  id: number;
  worker: SandboxWorker;
  start: string;
  finish: string;
  duration: number;
  cash: number;
  card: number;
  revenue: number;
  goods: number;
  services: number;
  bonuses: number;
  tips: number;
  refunds: number;
}

const shiftsForModel = (
  model: SandboxReportModel,
  workers: SandboxWorker[]
): GeneratedShift[] => {
  const shifts = model.days.flatMap((day) => {
    const epochDay = Math.floor(day.date.getTime() / 86_400_000);
    const revenueParts = splitReportValue(day.totalRevenue, [0.46, 0.54], 2);
    const cashParts = splitReportValue(day.cashRevenue, [0.48, 0.52], 2);
    const cardParts = revenueParts.map((value, index) =>
      reportMoney(Math.max(0, value - cashParts[index]))
    );

    return [0, 1].map((slot): GeneratedShift => {
      const worker = workers[(epochDay + slot) % workers.length];
      const start = new Date(
        day.date.getTime() + (slot === 0 ? 9 : 21) * 3_600_000
      );
      const finish = new Date(start.getTime() + 12 * 3_600_000);
      const revenue = revenueParts[slot];
      return {
        id: epochDay * 10 + slot,
        worker,
        start: start.toISOString(),
        finish: finish.toISOString(),
        duration: 12 * 3600,
        cash: cashParts[slot],
        card: cardParts[slot],
        revenue,
        goods: reportMoney((revenue * day.goodsRevenue) / day.totalRevenue),
        services: reportMoney(
          (revenue * day.serviceRevenue) / day.totalRevenue
        ),
        bonuses: reportMoney(day.bonusesSpent / 2),
        tips:
          Math.round(createReportRandom(`tips:${epochDay}:${slot}`)() * 8) * 50,
        refunds: reportMoney(revenue * 0.006),
      };
    });
  });
  return shifts.sort(
    (left, right) => Date.parse(right.start) - Date.parse(left.start)
  );
};

const workShiftsHandler: SandboxOperationHandler = (variables, store) => {
  const model = modelFor(variables, store);
  const requestedWorker = String(inputObject(variables).workerUuid ?? '');
  const shifts = shiftsForModel(model, store.getState().workers).filter(
    (shift) => !requestedWorker || shift.worker.uuid === requestedWorker
  );
  const report = {
    labels: [],
    extraLabels: shifts.map((shift) => workShiftFio(shift.worker)),
    extraLabels2: shifts.map((shift) => shift.start),
    extraLabels3: shifts.map((shift) => shift.finish),
    keys: shifts.map((shift) => String(shift.id)),
    data: shifts.map((shift) => ({
      values: [
        shift.duration,
        shift.refunds,
        shift.revenue,
        shift.card,
        shift.cash,
        0,
        0,
        0,
        0,
        shift.bonuses,
        0,
        0,
        shift.tips,
      ],
    })),
    url: csvUrl([
      ['Смена', 'Начало', 'Конец', 'Сотрудник', 'Наличные', 'Карта', 'Выручка'],
      ...shifts.map((shift) => [
        shift.id,
        shift.start,
        shift.finish,
        `${shift.worker.last_name} ${shift.worker.first_name}`,
        shift.cash,
        shift.card,
        shift.revenue,
      ]),
    ]),
  };
  const byWorker = new Map<string, GeneratedShift[]>();
  shifts.forEach((shift) => {
    const list = byWorker.get(shift.worker.uuid) ?? [];
    list.push(shift);
    byWorker.set(shift.worker.uuid, list);
  });
  const workerGroups = Array.from(byWorker.values());
  const summaryReport = {
    labels: [],
    extraLabels: [],
    extraLabels2: [],
    extraLabels3: [],
    keys: workerGroups.map((items) => workShiftFio(items[0].worker)),
    data: workerGroups.map((items) => {
      const total = (selector: (shift: GeneratedShift) => number): number =>
        reportMoney(items.reduce((sum, item) => sum + selector(item), 0));
      return {
        values: [
          total((item) => item.duration),
          total((item) => item.refunds),
          total((item) => item.revenue),
          total((item) => item.goods),
          total((item) => item.services),
          0,
          0,
          total((item) => item.bonuses),
          0,
          0,
          items.length,
          total((item) => item.tips),
        ],
      };
    }),
    url: csvUrl([
      ['Сотрудник', 'Смен', 'Выручка'],
      ...workerGroups.map((items) => [
        `${items[0].worker.last_name} ${items[0].worker.first_name}`,
        items.length,
        reportMoney(items.reduce((sum, item) => sum + item.revenue, 0)),
      ]),
    ]),
  };
  return {
    workShiftsReport: report,
    workShiftsSummaryReport: summaryReport,
  };
};

const shiftDetail = (
  id: number,
  store: Parameters<SandboxOperationHandler>[1]
): GeneratedShift => {
  const epochDay = Math.floor(id / 10);
  const slot = Math.abs(id % 10) % 2;
  const day = new Date(epochDay * 86_400_000);
  const model = createSandboxReportModel(
    { input: { from: formatIsoDay(day), to: formatIsoDay(day) } },
    store
  );
  return shiftsForModel(model, store.getState().workers).find(
    (shift) => shift.id === epochDay * 10 + slot
  ) as GeneratedShift;
};

const workShiftByIdHandler: SandboxOperationHandler = (variables, store) => {
  const shift = shiftDetail(Number(variables.id), store);
  return {
    workShift: {
      id: shift.id,
      worker: shift.worker,
      created_at: shift.start,
      finished_at: shift.finish,
      money: {
        cash_on_start: 10_000,
        sum: {
          cash: shift.cash,
          card: shift.card,
          deposit: reportMoney(shift.revenue * 0.58),
          bonus: shift.bonuses,
        },
      },
      payments: [],
      cashOrders: [],
      comment: 'Смена сформирована локальной песочницей',
    },
  };
};

const workShiftDataHandler: SandboxOperationHandler = (variables, store) => {
  const shift = shiftDetail(Number(variables.id), store);
  const goods = store.getState().goods.slice(0, 4);
  const services = store.getState().services.slice(0, 3);
  return {
    getWorkShiftPaymentOverviewData: {
      id: shift.id,
      total: shift.revenue,
      cash: shift.cash,
      card: shift.card,
      deposit: reportMoney(shift.revenue * 0.58),
      online_deposit: reportMoney(shift.revenue * 0.2),
      bonus: shift.bonuses,
      cash_on_start: 10_000,
      created_at: shift.start,
      finished_at: shift.finish,
      sum: {
        good: shift.goods,
        service: shift.services,
        tariff: reportMoney(shift.revenue - shift.goods - shift.services),
        deposit: reportMoney(shift.revenue * 0.58),
        tips: shift.tips,
      },
      cash_orders: [],
      worker: shift.worker,
      goods: goods.map((good, index) => ({
        title: good.title,
        cost: good.cost,
        amount: index + 1,
        in_stock: good.amount,
        sum: good.cost * (index + 1),
      })),
      services: services.map((service) => ({
        title: service.title,
        amount: 1,
        sum: service.cost,
      })),
      combos: [],
    },
    workShift: { comment: 'Смена сформирована локальной песочницей' },
  };
};

const overviewHandler: SandboxOperationHandler = (variables, store) => {
  const model = modelFor(variables, store);
  const { totals } = model;
  const bonusParts = splitReportValue(
    totals.bonusesAccrued,
    [0.18, 0.44, 0.29, 0.09],
    2
  );
  const clientBuckets = splitReportValue(
    totals.uniqueClients,
    [0.46, 0.24, 0.17, 0.1, 0.03]
  );
  const loadHours = splitReportValue(
    totals.availableSeconds / 3600,
    [0.04, 0.24, 0.46, 0.24, 0.02]
  );
  const report = (
    title: string,
    labels: string[],
    values: number[],
    reportSummary: UnifiedSummary[]
  ): UnifiedReport => ({
    title,
    labels,
    keys: [],
    extraLabels: [],
    extraLabels2: [],
    extraLabels3: [],
    points: values.length,
    data: [{ values }],
    summary: reportSummary,
    url: null,
  });

  return {
    overviewReport: {
      data: [
        report(
          'Финансовые показатели',
          ['cash_revenue', 'card_revenue', 'online_revenue'],
          [totals.cashRevenue, totals.cardRevenue, totals.onlineRevenue],
          [
            summary('total_revenue', totals.totalRevenue),
            summary(
              'avg_revenue_per_host',
              reportMoney(totals.totalRevenue / model.hostCount)
            ),
            summary('pko_amount', totals.incomingCashOrders),
            summary('rko_amount', totals.outgoingCashOrders),
            summary(
              'deposit_revenue',
              totals.depositRevenue,
              reportMoney((totals.depositRevenue / totals.totalRevenue) * 100)
            ),
          ]
        ),
        report(
          'Распределение расходов клиентов',
          ['tariff_revenue', 'goods_revenue', 'service_revenue'],
          [totals.tariffRevenue, totals.goodsRevenue, totals.serviceRevenue],
          []
        ),
        report(
          'Распределение начисленных бонусов',
          [
            'bonus_cashback',
            'bonus_manager',
            'bonus_achievement',
            'bonus_promocode',
          ],
          bonusParts,
          [
            summary('total_bonus', totals.bonusesAccrued),
            summary(
              'total_bonus_with_refunds',
              totals.bonusRefunds,
              reportMoney((totals.bonusRefunds / totals.bonusesAccrued) * 100)
            ),
          ]
        ),
        report(
          'Распределение клиентов по количеству визитов',
          ['1', '2', '3-4', '5-9', '10+'],
          clientBuckets,
          [
            summary('unique_clients', totals.uniqueClients),
            summary('new_clients_count', totals.newClients),
            summary(
              'new_clients_percent',
              reportMoney((totals.newClients / totals.uniqueClients) * 100)
            ),
            summary('repeat_visits', totals.repeatVisitors),
            summary(
              'repeat_visits_percent',
              reportMoney((totals.repeatVisitors / totals.uniqueClients) * 100)
            ),
            summary('guest_sessions', totals.guestSessions),
            summary(
              'guest_sessions_percent',
              reportMoney((totals.guestSessions / totals.sessions) * 100)
            ),
            summary(
              'client_apru',
              reportMoney(totals.totalRevenue / totals.uniqueClients)
            ),
          ]
        ),
        report(
          'Операционные показатели',
          ['0', '1-30', '31-60', '61-90', '91-100'],
          loadHours,
          [
            summary('total_load_percent', totals.occupationPercent),
            summary(
              'total_sessions',
              totals.sessions,
              reportMoney(totals.averageSessionSeconds / 3600)
            ),
            summary(
              'avg_hour_cost',
              reportMoney(
                totals.tariffRevenue / (totals.occupiedSeconds / 3600)
              )
            ),
            summary(
              'total_booked_sessions',
              totals.bookedSessions,
              reportMoney(totals.bookedSeconds / 3600)
            ),
            summary(
              'total_booked_revenue',
              totals.bookedRevenue,
              reportMoney(totals.bookedRevenue / totals.bookedSessions)
            ),
          ]
        ),
        report(
          'Товары',
          [],
          [],
          [
            summary('total_goods_sold', totals.soldGoods),
            summary(
              'avg_goods_receipt',
              reportMoney(totals.goodsRevenue / Math.max(1, totals.soldGoods))
            ),
          ]
        ),
      ],
      url: csvUrl([
        ['Показатель', 'Значение'],
        ['Выручка', totals.totalRevenue],
        ['Сеансы', totals.sessions],
        ['Загрузка, %', totals.occupationPercent],
        ['Уникальные клиенты', totals.uniqueClients],
      ]),
    },
  };
};

const topItemsHandler: SandboxOperationHandler = (variables, store) => {
  const model = modelFor(variables, store);
  const state = store.getState();
  const goods = state.goods.slice(0, 5);
  const tariffs = state.tariffs.slice(0, 5);
  const services = state.services.slice(0, 5);
  const entries: Array<{
    key: string;
    title: string;
    sum: number;
    quantity: number;
    image: string | null;
  }> = [];
  const append = (
    key: string,
    items: Array<{ title: string; cost: number }>,
    total: number,
    count: number
  ): void => {
    const values = splitReportValue(
      total,
      items.map((_, index) => 6 - index),
      2
    );
    const quantities = splitReportValue(
      count,
      items.map((_, index) => 6 - index)
    );
    items.forEach((item, index) =>
      entries.push({
        key,
        title: item.title,
        sum: values[index],
        quantity: Math.max(1, quantities[index]),
        image: null,
      })
    );
  };
  append('GOOD', goods, model.totals.goodsRevenue, model.totals.soldGoods);
  append('TARIFF', tariffs, model.totals.tariffRevenue, model.totals.sessions);
  append(
    'SERVICE',
    services,
    model.totals.serviceRevenue,
    Math.max(services.length, Math.round(model.totals.sessions * 0.1))
  );
  return {
    topSoldOverviewItemsReport: {
      data: [],
      extraLabels: [],
      extraLabels2: [],
      extraLabels3: [],
      keys: entries.map((item) => item.key),
      labels: [],
      points: entries.length,
      summary: entries.map((item) =>
        summary(item.title, item.sum, item.quantity, item.image)
      ),
      title: 'Товары',
      url: null,
    },
  };
};

const clubLoadHandler: SandboxOperationHandler = (variables, store) => {
  const model = modelFor(variables, store);
  const values: UnifiedData[] = [];
  for (let weekday = 1; weekday <= 7; weekday += 1) {
    const dayFactor = weekday >= 5 ? 1.12 : weekday === 1 ? 0.88 : 0.96;
    for (let hour = 0; hour < 24; hour += 1) {
      const peak = Math.exp(-Math.pow(hour - 20, 2) / 35);
      const night = Math.exp(-Math.pow(hour - 1, 2) / 18);
      const load = Math.min(
        model.hostCount,
        Math.max(
          1,
          Math.round(
            model.hostCount * (0.08 + 0.64 * peak + 0.18 * night) * dayFactor
          )
        )
      );
      values.push({ values: [weekday, hour, load] });
    }
  }
  return { clubLoadByDayOfWeekReport: { data: values } };
};

const groupVisitorDays = (
  days: SandboxDailyReportFact[],
  slice: string
): SandboxDailyReportFact[][] => {
  if (slice === 'DAY') return days.map((day) => [day]);
  const groups = new Map<string, SandboxDailyReportFact[]>();
  days.forEach((day) => {
    const date = day.date;
    const key =
      slice === 'MONTH'
        ? `${date.getUTCFullYear()}-${date.getUTCMonth()}`
        : `${date.getUTCFullYear()}-${Math.floor(
            (date.getTime() - Date.UTC(date.getUTCFullYear(), 0, 1)) /
              86_400_000 /
              7
          )}`;
    const group = groups.get(key) ?? [];
    group.push(day);
    groups.set(key, group);
  });
  return Array.from(groups.values());
};

const visitorsHandler: SandboxOperationHandler = (variables, store) => {
  const model = modelFor(variables, store);
  const slice = String(inputObject(variables).slice ?? 'DAY');
  const groups = groupVisitorDays(model.days, slice);
  const keys = groups.map((group) => {
    const first = formatVisitorsDay(group[0].date);
    const last = formatVisitorsDay(group[group.length - 1].date);
    return first === last ? first : `${first} - ${last}`;
  });
  const data = groups.map((group) => {
    const total = (selector: (day: SandboxDailyReportFact) => number): number =>
      group.reduce((sum, day) => sum + selector(day), 0);
    const clients = Math.round(total((day) => day.uniqueVisitors) * 0.82);
    const guests = total((day) => day.guestSessions);
    return {
      values: [
        clients,
        guests,
        clients + guests,
        total((day) => day.newVisitors),
        total((day) => day.clientSessions),
        total((day) => day.guestSessions),
      ],
    };
  });
  const report = {
    labels: ['Клиенты', 'Гости'],
    keys,
    points: data.length,
    data,
    summary: [
      {
        title: 'total',
        value: model.totals.uniqueClients + model.totals.guestSessions,
      },
      { title: 'users', value: model.totals.uniqueClients },
      { title: 'guests', value: model.totals.guestSessions },
      { title: 'new', value: model.totals.newClients },
      { title: 'user_sessions', value: model.totals.clientSessions },
    ],
    url: csvUrl([
      ['Период', 'Клиенты', 'Гости', 'Сеансы'],
      ...data.map((item, index) => [
        keys[index],
        item.values[0],
        item.values[1],
        item.values[4] + item.values[5],
      ]),
    ]),
  };
  return { uniqueUsersReport: report };
};

const hostsHandler: SandboxOperationHandler = (variables, store) => {
  const model = modelFor(variables, store);
  const hosts = store.getState().hosts;
  const weights = hosts.map(
    (host) => 0.85 + (hashReportKey(host.alias) % 35) / 100
  );
  const times = splitReportValue(model.totals.occupiedSeconds, weights);
  const sessions = splitReportValue(model.totals.sessions, weights);
  const proceeds = splitReportValue(model.totals.tariffRevenue, weights, 2);
  const availablePerHost = model.period.dayCount * 24 * 3600;
  const data = hosts.map((host, index) => {
    const guestTime = Math.round(times[index] * 0.055);
    const clientTime = times[index] - guestTime;
    const guestProceeds = reportMoney(proceeds[index] * 0.045);
    const clientProceeds = reportMoney(proceeds[index] - guestProceeds);
    return {
      values: [
        guestTime,
        clientTime,
        times[index],
        reportMoney((guestTime / availablePerHost) * 100),
        reportMoney((clientTime / availablePerHost) * 100),
        reportMoney((times[index] / availablePerHost) * 100),
        sessions[index] ? Math.round(times[index] / sessions[index]) : 0,
        sessions[index],
        guestProceeds,
        clientProceeds,
        proceeds[index],
      ],
    };
  });
  const groups = new Map(
    store.getState().hostGroups.map((group) => [group.id, group.title])
  );
  const report = {
    labels: [],
    extraLabels: hosts.map(
      (host) => groups.get(host.group_id) ?? 'Основной зал'
    ),
    keys: hosts.map((host) => host.alias),
    points: hosts.length,
    data,
    summary: [
      { title: 'total working time', value: model.totals.availableSeconds },
      { title: 'total occupation time', value: model.totals.occupiedSeconds },
      {
        title: 'total occupation percentage',
        value: model.totals.occupationPercent,
      },
      { title: 'total client sessions count', value: model.totals.sessions },
      {
        title: 'average client sessions time',
        value: model.totals.averageSessionSeconds,
      },
      { title: 'total money', value: model.totals.tariffRevenue },
    ],
    url: csvUrl([
      ['Хост', 'Зал', 'Сеансы', 'Занятость, %', 'Выручка'],
      ...hosts.map((host, index) => [
        host.alias,
        groups.get(host.group_id) ?? '',
        sessions[index],
        data[index].values[5],
        proceeds[index],
      ]),
    ]),
  };
  return { hostsOccupationReport: report };
};

const applicationsHandler: SandboxOperationHandler = (variables, store) => {
  const model = modelFor(variables, store);
  const input = inputObject(variables);
  const type = String(input.type ?? 'APPLICATION');
  const groupId = Number(input.hostGroupId ?? 0);
  const hostId = Number(input.hostId ?? 0);
  const state = store.getState();
  const filteredHosts = hostId
    ? state.hosts.filter((host) => host.id === hostId)
    : groupId
    ? state.hosts.filter((host) => host.group_id === groupId)
    : state.hosts;
  const scale = Math.max(
    1 / model.hostCount,
    filteredHosts.length / model.hostCount
  );
  const source =
    type === 'APPLICATION'
      ? state.shortcuts
          .filter((shortcut) => shortcut.type === 'GAME')
          .slice(0, 18)
      : type === 'WINDOW'
      ? [
          'Counter-Strike 2',
          'Dota 2',
          'Discord',
          'Google Chrome',
          'Steam',
          'Valorant',
          'VK Play',
          'Telegram',
        ].map((title, id) => ({ id, title }))
      : [
          'Играть',
          'Открыть Steam',
          'Пополнить баланс',
          'Выбрать тариф',
          'Вызвать администратора',
          'Завершить сеанс',
        ].map((title, id) => ({ id, title }));
  const totalLaunches = Math.max(
    1,
    Math.round(model.totals.sessions * 2.4 * scale)
  );
  const weights = source.map((_, index) => Math.max(1, source.length - index));
  const launches = splitReportValue(totalLaunches, weights);
  const times = splitReportValue(model.totals.occupiedSeconds * scale, weights);
  const report = {
    labels: source.map((item) => item.title),
    extraLabels: [],
    keys: source.map((item) => item.title),
    points: source.length,
    data: source.map((_, index) => ({
      values: [
        launches[index],
        times[index],
        reportMoney(
          (times[index] / Math.max(1, model.totals.occupiedSeconds * scale)) *
            100
        ),
      ],
    })),
    summary: source.map((item, index) => ({
      title: item.title,
      value: launches[index],
    })),
    url: csvUrl([
      ['Приложение', 'Запуски', 'Время, сек.', 'Доля, %'],
      ...source.map((item, index) => [
        item.title,
        launches[index],
        times[index],
        reportMoney(
          (times[index] / Math.max(1, model.totals.occupiedSeconds * scale)) *
            100
        ),
      ]),
    ]),
  };
  return { applicationsReport: report };
};

interface SalesRow {
  title: string;
  category: string;
  sold: number;
  sum: number;
  average: number;
  refunds: number;
}

const salesRows = (
  model: SandboxReportModel,
  store: Parameters<SandboxOperationHandler>[1]
): SalesRow[] => {
  const state = store.getState();
  const rows: SalesRow[] = [];
  const appendItems = (
    category: string,
    items: Array<{ title: string; cost: number }>,
    totalSum: number,
    totalSold: number
  ): void => {
    const weights = items.map((_, index) => Math.max(1, items.length - index));
    const sums = splitReportValue(totalSum, weights, 2);
    const sold = splitReportValue(totalSold, weights);
    items.forEach((item, index) =>
      rows.push({
        title: item.title,
        category,
        sold: Math.max(1, sold[index]),
        sum: sums[index],
        average: reportMoney(sums[index] / Math.max(1, sold[index])),
        refunds: index === 0 ? Math.round(sold[index] * 0.01) : 0,
      })
    );
  };
  rows.push({
    title: 'DEPOSIT',
    category: 'DEPOSIT',
    sold: model.totals.uniqueClients,
    sum: model.totals.depositRevenue,
    average: reportMoney(
      model.totals.depositRevenue / model.totals.uniqueClients
    ),
    refunds: 0,
  });
  rows.push({
    title: 'BONUS',
    category: 'BONUS',
    sold: model.totals.uniqueClients,
    sum: model.totals.bonusesSpent,
    average: reportMoney(
      model.totals.bonusesSpent / model.totals.uniqueClients
    ),
    refunds: 0,
  });
  appendItems(
    'TARIFF',
    state.tariffs.slice(0, 8),
    model.totals.tariffRevenue,
    model.totals.sessions
  );
  appendItems(
    'GOOD',
    state.goods.slice(0, 12),
    model.totals.goodsRevenue,
    model.totals.soldGoods
  );
  appendItems(
    'SERVICE',
    state.services.slice(0, 6),
    model.totals.serviceRevenue,
    Math.max(6, Math.round(model.totals.sessions * 0.1))
  );
  rows.push({
    title: 'CLOUD_PAYMENTS',
    category: 'CLOUD_PAYMENTS',
    sold: Math.round(model.totals.uniqueClients * 0.18),
    sum: reportMoney(model.totals.onlineRevenue * 0.62),
    average: reportMoney(
      model.totals.onlineRevenue /
        Math.max(1, model.totals.uniqueClients * 0.18)
    ),
    refunds: 0,
  });
  rows.push({
    title: 'TINKOFF_SBP',
    category: 'TINKOFF_SBP',
    sold: Math.round(model.totals.uniqueClients * 0.1),
    sum: reportMoney(model.totals.onlineRevenue * 0.38),
    average: reportMoney(
      model.totals.onlineRevenue / Math.max(1, model.totals.uniqueClients * 0.1)
    ),
    refunds: 0,
  });
  return rows;
};

const salesHandler: SandboxOperationHandler = (variables, store) => {
  const model = modelFor(variables, store);
  const rows = salesRows(model, store);
  const categoryTotals = (category: string): [number, number] => [
    reportMoney(
      rows
        .filter((row) => row.category === category)
        .reduce((sum, row) => sum + row.sum, 0)
    ),
    rows
      .filter((row) => row.category === category)
      .reduce((sum, row) => sum + row.sold, 0),
  ];
  const fields: Array<[string, string]> = [
    ['deposit', 'DEPOSIT'],
    ['bonus', 'BONUS'],
    ['tariff', 'TARIFF'],
    ['good', 'GOOD'],
    ['service', 'SERVICE'],
    ['cloudpayments', 'CLOUD_PAYMENTS'],
    ['tinkoff sbp', 'TINKOFF_SBP'],
  ];
  const reportSummary = fields.flatMap(([key, category]) => {
    const [sum, count] = categoryTotals(category);
    return [
      { title: `${key} sum`, value: sum },
      { title: `${key} count`, value: count },
    ];
  });
  return {
    salesReport: {
      labels: [],
      extraLabels: rows.map((row) => row.category),
      keys: rows.map((row) => row.title),
      points: rows.length,
      data: rows.map((row) => ({
        values: [row.sold, row.sum, row.average, row.refunds],
      })),
      summary: reportSummary,
      url: csvUrl([
        [
          'Позиция',
          'Категория',
          'Продано',
          'Сумма',
          'Средняя цена',
          'Возвраты',
        ],
        ...rows.map((row) => [
          row.title,
          row.category,
          row.sold,
          row.sum,
          row.average,
          row.refunds,
        ]),
      ]),
    },
  };
};

const syntheticClient = (
  index: number,
  model: SandboxReportModel,
  storeClients: SandboxClient[]
): SandboxClient => {
  if (index < storeClients.length) return storeClients[index];
  const firstNames = [
    'Александр',
    'Мария',
    'Дмитрий',
    'Анна',
    'Максим',
    'Елена',
    'Илья',
    'София',
  ];
  const lastNames = [
    'Смирнов',
    'Кузнецов',
    'Волков',
    'Орлов',
    'Попов',
    'Лебедев',
    'Морозов',
    'Соколов',
  ];
  const seed = hashReportKey(`${model.period.key}:client:${index}`);
  const firstName = firstNames[seed % firstNames.length];
  const lastName = lastNames[Math.floor(seed / 7) % lastNames.length];
  const created = new Date(
    model.period.from.getTime() - ((seed % 720) + 30) * 86_400_000
  ).toISOString();
  return {
    ...storeClients[index % storeClients.length],
    id: 10_000 + index,
    uuid: `report:client:${model.period.key}:${index}`,
    nickname: `${
      ['fox', 'neo', 'storm', 'pixel', 'raiden', 'night'][seed % 6]
    }_${String(index + 1).padStart(3, '0')}`,
    phone: `+7998${String(10_000_000 + index).slice(-7)}`,
    phone_suffix: String(10_000_000 + index).slice(-6),
    first_name: firstName,
    last_name: lastName,
    created_at: created,
    deposit: seed % 4000,
    bonus: seed % 900,
    user_discount: seed % 5 === 0 ? 5 : 0,
    avatar_url: '',
  };
};

const clientsHandler: SandboxOperationHandler = (variables, store) => {
  const model = modelFor(variables, store);
  const input = inputObject(variables);
  const first = Math.max(1, Number(variables.first ?? 25));
  const currentPage = Math.max(1, Number(variables.page ?? 1));
  const search = String(
    (input.search as Record<string, unknown> | undefined)?.q ?? ''
  )
    .trim()
    .toLowerCase();
  const sort = (input.sorting as Record<string, unknown> | undefined) ?? {};
  const ascending = String(sort.direction ?? 'DESC').toUpperCase() === 'ASC';
  const totalClients = Math.max(
    store.getState().clients.length,
    model.totals.uniqueClients
  );
  const searchableCount = search ? Math.min(totalClients, 5000) : totalClients;
  let indexes = Array.from({ length: searchableCount }, (_, index) => index);
  if (ascending) indexes.reverse();
  if (search) {
    indexes = indexes.filter((index) => {
      const client = syntheticClient(index, model, store.getState().clients);
      return [
        client.nickname,
        client.phone,
        client.first_name,
        client.last_name,
      ]
        .join(' ')
        .toLowerCase()
        .includes(search);
    });
  }
  const resultTotal = search ? indexes.length : totalClients;
  const selectedIndexes = indexes.slice(
    (currentPage - 1) * first,
    currentPage * first
  );
  const clientRevenue = model.totals.totalRevenue * 0.94;
  const weightSum = (totalClients * (totalClients + 1)) / 2;
  const data = selectedIndexes.map((index) => {
    const client = syntheticClient(index, model, store.getState().clients);
    const weight = totalClients - index;
    const total = reportMoney((clientRevenue * weight) / weightSum);
    const tariff = reportMoney(total * 0.74);
    const shop = reportMoney(total - tariff);
    const visits = Math.max(
      1,
      Math.round(model.period.dayCount * (0.25 + (index % 7) * 0.08))
    );
    return {
      user: client,
      total: { value: total },
      tariff: { value: tariff },
      shop: { value: shop },
      average: { value: reportMoney(total / visits) },
    };
  });
  return {
    clientsPaymentReport: {
      data,
      paginatorInfo: {
        currentPage,
        lastPage: Math.max(1, Math.ceil(resultTotal / first)),
      },
    },
  };
};

const transfers = (
  model: SandboxReportModel,
  store: Parameters<SandboxOperationHandler>[1]
): Array<{
  transfer: {
    club: { id: number; name: string };
    sum: number;
    type: string;
    created_at: string;
  };
  user: {
    avatar_url: string;
    phone: string;
    middle_name: string;
    last_name: string;
    first_name: string;
  };
}> => {
  const clubs = [
    { id: 2, name: 'Smart Arena Сокол' },
    { id: 3, name: 'Cyber Point Арбат' },
    { id: 4, name: 'GG Club Таганская' },
  ];
  const count = Math.min(120, Math.max(3, model.period.dayCount * 2));
  return Array.from({ length: count }, (_, index) => {
    const random = createReportRandom(`${model.period.key}:transfer:${index}`);
    const client = syntheticClient(
      index % model.totals.uniqueClients,
      model,
      store.getState().clients
    );
    const type = random() > 0.48 ? 'DEPOSIT' : 'WITHDRAW';
    const date = new Date(
      model.period.from.getTime() +
        Math.floor(random() * model.period.dayCount) * 86_400_000 +
        Math.floor(random() * 24) * 3_600_000
    ).toISOString();
    return {
      transfer: {
        club: clubs[index % clubs.length],
        sum: Math.round((400 + random() * 2600) / 50) * 50,
        type,
        created_at: date,
      },
      user: {
        avatar_url: client.avatar_url,
        phone: client.phone,
        middle_name: client.middle_name,
        last_name: client.last_name,
        first_name: client.first_name,
      },
    };
  });
};

const transfersHandler: SandboxOperationHandler = (variables, store) => {
  const model = modelFor(variables, store);
  const input = inputObject(variables);
  const requestedType = String(input.type ?? '');
  const query = String(
    (input.search as Record<string, unknown> | undefined)?.q ?? ''
  ).toLowerCase();
  const all = transfers(model, store).filter(
    (item) =>
      (!requestedType || item.transfer.type === requestedType) &&
      (!query || JSON.stringify(item.user).toLowerCase().includes(query))
  );
  const first = Math.max(1, Number(variables.first ?? 50));
  const currentPage = Math.max(1, Number(variables.page ?? 1));
  return {
    depositTransferReport: {
      data: all.slice((currentPage - 1) * first, currentPage * first),
      paginatorInfo: {
        currentPage,
        lastPage: Math.max(1, Math.ceil(all.length / first)),
      },
      url: csvUrl([
        ['Дата', 'Клуб', 'Клиент', 'Тип', 'Сумма'],
        ...all.map((item) => [
          item.transfer.created_at,
          item.transfer.club.name,
          item.user.phone,
          item.transfer.type,
          item.transfer.sum,
        ]),
      ]),
    },
  };
};

const transfersSummaryHandler: SandboxOperationHandler = (variables, store) => {
  const model = modelFor(variables, store);
  const all = transfers(model, store);
  const clubNames = Array.from(
    new Set(all.map((item) => item.transfer.club.name))
  );
  const rows = clubNames.map((name) => {
    const incoming = all
      .filter(
        (item) =>
          item.transfer.club.name === name && item.transfer.type === 'DEPOSIT'
      )
      .reduce((sum, item) => sum + item.transfer.sum, 0);
    const outgoing = all
      .filter(
        (item) =>
          item.transfer.club.name === name && item.transfer.type === 'WITHDRAW'
      )
      .reduce((sum, item) => sum + item.transfer.sum, 0);
    return { name, incoming, outgoing, remains: incoming - outgoing };
  });
  const incoming = rows.reduce((sum, row) => sum + row.incoming, 0);
  const outgoing = rows.reduce((sum, row) => sum + row.outgoing, 0);
  return {
    depositTransferSummaryReport: {
      data: rows.map((row) => ({
        values: [row.incoming, row.outgoing, row.remains],
      })),
      extraLabels: rows.map((row) => row.name),
      summary: [
        { title: 'incoming sum', value: incoming },
        {
          title: 'incoming users',
          value: all.filter((item) => item.transfer.type === 'DEPOSIT').length,
        },
        { title: 'outgoing sum', value: outgoing },
        {
          title: 'outgoing users',
          value: all.filter((item) => item.transfer.type === 'WITHDRAW').length,
        },
        { title: 'total', value: incoming - outgoing },
      ],
    },
  };
};

const bonusSummaryHandler: SandboxOperationHandler = (variables, store) => {
  const model = modelFor(variables, store);
  const sourceValues = splitReportValue(
    model.totals.bonusesAccrued,
    [0.38, 0.24, 0.29, 0.09],
    2
  );
  return {
    bonusDetailedSummaryReport: {
      data: [{ values: sourceValues }],
      labels: ['Кешбек', 'Прямые начисления', 'Достижения', 'Промокоды'],
      summary: [
        summary('Всего начислено', model.totals.bonusesAccrued),
        summary('Всего потрачено', model.totals.bonusesSpent),
        summary('Уникальных пользователей', model.totals.uniqueClients),
        summary('Процент пользователей с бонусами', 62.5),
        summary(
          'Средний депозит на пользователя',
          reportMoney(model.totals.bonusesAccrued / model.totals.uniqueClients)
        ),
        summary(
          'Средний расход на пользователя',
          reportMoney(model.totals.bonusesSpent / model.totals.uniqueClients)
        ),
        summary(
          'Процент суммы платежей с бонусами',
          reportMoney(
            (model.totals.bonusesSpent / model.totals.totalRevenue) * 100
          )
        ),
      ],
      url: csvUrl([
        ['Источник', 'Начислено'],
        ['Кешбек', sourceValues[0]],
        ['Прямые начисления', sourceValues[1]],
        ['Достижения', sourceValues[2]],
        ['Промокоды', sourceValues[3]],
      ]),
    },
  };
};

const bonusSeriesHandler: SandboxOperationHandler = (variables, store) => {
  const model = modelFor(variables, store);
  const slice = String(inputObject(variables).groupBy ?? 'DAY');
  const groups = groupVisitorDays(model.days, slice);
  return {
    bonusDetailedTimeSeriesReport: {
      keys: groups.map((group) => {
        const first = formatIsoDay(group[0].date);
        const last = formatIsoDay(group[group.length - 1].date);
        return `${first === last ? first : `${first} - ${last}`} 00:00:00`;
      }),
      data: groups.map((group) => ({
        values: [
          reportMoney(group.reduce((sum, day) => sum + day.bonusesAccrued, 0)),
          reportMoney(group.reduce((sum, day) => sum + day.bonusesSpent, 0)),
        ],
      })),
    },
  };
};

const usersBonusHandler: SandboxOperationHandler = (variables, store) => {
  const model = modelFor(variables, store);
  const count = Math.min(80, model.totals.uniqueClients);
  const accrued = splitReportValue(
    model.totals.bonusesAccrued,
    Array.from({ length: count }, (_, index) => count - index),
    2
  );
  const spent = splitReportValue(
    model.totals.bonusesSpent,
    Array.from({ length: count }, (_, index) => count - index),
    2
  );
  const clients = Array.from({ length: count }, (_, index) =>
    syntheticClient(index, model, store.getState().clients)
  );
  return {
    usersBonusReport: {
      labels: [],
      extraLabels: clients.map((client) => client.phone),
      extraLabels2: clients.map((client) => client.avatar_url),
      extraLabels3: clients.map(() => 'client'),
      keys: clients.map((client) => client.nickname),
      data: clients.map((_, index) => {
        const sources = splitReportValue(
          accrued[index],
          [0.24, 0.41, 0.26, 0.09],
          2
        );
        return { values: [...sources, accrued[index], spent[index]] };
      }),
      url: csvUrl([
        ['Клиент', 'Начислено', 'Потрачено'],
        ...clients.map((client, index) => [
          client.nickname,
          accrued[index],
          spent[index],
        ]),
      ]),
    },
  };
};

export const reportResolvers: Record<string, SandboxOperationHandler> = {
  workShiftsReport: workShiftsHandler,
  workShiftsReportFile: workShiftsHandler,
  workShiftsSummaryReportFile: workShiftsHandler,
  workShift: workShiftByIdHandler,
  workshiftData: workShiftDataHandler,
  OverviewReport: overviewHandler,
  TopSoldOverviewItemsReport: topItemsHandler,
  ClubLoadByDayOfWeekReport: clubLoadHandler,
  clientsPaymentReport: clientsHandler,
  uniqueUsersReport: visitorsHandler,
  uniqueUsersReportFile: visitorsHandler,
  hostsOccupationReport: hostsHandler,
  ApplicationsReport: applicationsHandler,
  salesReport: salesHandler,
  exportSalesReport: salesHandler,
  depositTransferReport: transfersHandler,
  depositTransferSummaryReport: transfersSummaryHandler,
  BonusDetailedSummaryReport: bonusSummaryHandler,
  BonusDetailedTimeSeriesReport: bonusSeriesHandler,
  UsersBonusReport: usersBonusHandler,
};
